import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { AuthStore } from '../../shared/stores/auth.store';
import { AppNavigationService } from './app-navigation.service';

/** localStorage key used to broadcast activity timestamps across browser tabs. */
const LAST_ACTIVITY_KEY = 'session_last_activity';

/** Milliseconds of inactivity before the warning modal is shown (13 minutes). */
const WARNING_MS = 13 * 60 * 1000;

/** Milliseconds of inactivity before the session is terminated (15 minutes). */
const LOGOUT_MS = 15 * 60 * 1000;

/** DOM events that constitute user activity. */
const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
];

/**
 * Manages session inactivity timeout for authenticated users.
 *
 * Behaviour:
 * - After {@link WARNING_MS} of inactivity, sets `showWarning` to `true` to prompt
 *   the user to stay signed in.
 * - After {@link LOGOUT_MS} of inactivity, automatically logs the user out and
 *   redirects to `/login?expired=true`.
 * - Activity in any browser tab resets the timers via a `localStorage` storage event.
 * - Timers only run while `AuthStore.isAuthenticated` is `true`.
 */
@Injectable({ providedIn: 'root' })
export class SessionTimeoutService {
  private readonly authStore = inject(AuthStore);
  private readonly appNav = inject(AppNavigationService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _showWarning = signal<boolean>(false);

  /** `true` when the inactivity warning modal should be displayed. */
  readonly showWarning = this._showWarning.asReadonly();

  /** `true` while the user has an active authenticated session. */
  readonly isActive = computed(() => this.authStore.isAuthenticated());

  private _warningTimerId: ReturnType<typeof setTimeout> | null = null;
  private _logoutTimerId: ReturnType<typeof setTimeout> | null = null;

  /** Registered callback to auto-save a dirty form draft before session expiry. */
  private _autoDraftSaveFn: (() => void) | null = null;

  private readonly _boundActivityHandler = () => this._onActivity();
  private readonly _boundStorageHandler = (e: StorageEvent) => this._onStorageEvent(e);

  constructor() {
    // Start/stop timers reactively as the authenticated state changes.
    effect(() => {
      if (this.isActive()) {
        this._attachListeners();
        this._resetTimers();
      } else {
        this._detachListeners();
        this._clearTimers();
      }
    });

    // Clean up on service destruction (e.g. during testing).
    this.destroyRef.onDestroy(() => {
      this._detachListeners();
      this._clearTimers();
    });
  }

  /**
   * Resets the inactivity timers and dismisses the warning modal.
   * Called when the user explicitly clicks "Stay Signed In".
   */
  staySignedIn(): void {
    this._showWarning.set(false);
    this._broadcastActivity();
    this._resetTimers();
  }

  /**
   * Immediately logs the user out without the expired-session query param.
   * Called when the user explicitly clicks "Sign Out" in the warning modal.
   */
  signOut(): void {
    this._showWarning.set(false);
    this._performLogout(false);
  }

  /**
   * Registers a callback to auto-save the form draft when the session
   * inactivity warning fires. The form page calls this on init and clears on destroy.
   */
  registerDraftAutoSave(fn: () => void): void {
    this._autoDraftSaveFn = fn;
  }

  /** Clears the registered draft auto-save callback. */
  clearDraftAutoSave(): void {
    this._autoDraftSaveFn = null;
  }

  // ── Private helpers ────────────────────────────────────────────────────────

  private _onActivity(): void {
    if (!this.isActive()) return;
    this._broadcastActivity();
    this._resetTimers();
  }

  private _onStorageEvent(event: StorageEvent): void {
    if (event.key === LAST_ACTIVITY_KEY && this.isActive()) {
      // Activity detected in another tab — reset timers without re-broadcasting.
      this._showWarning.set(false);
      this._resetTimers();
    }
  }

  private _broadcastActivity(): void {
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
  }

  private _resetTimers(): void {
    this._clearTimers();

    this._warningTimerId = setTimeout(() => {
      this._showWarning.set(true);
      // Auto-save any dirty form draft before the session fully expires
      if (this._autoDraftSaveFn) {
        this._autoDraftSaveFn();
      }
    }, WARNING_MS);

    this._logoutTimerId = setTimeout(() => {
      this._showWarning.set(false);
      this._performLogout(true);
    }, LOGOUT_MS);
  }

  private _clearTimers(): void {
    if (this._warningTimerId !== null) {
      clearTimeout(this._warningTimerId);
      this._warningTimerId = null;
    }
    if (this._logoutTimerId !== null) {
      clearTimeout(this._logoutTimerId);
      this._logoutTimerId = null;
    }
  }

  /**
   * Single logout path used by both manual sign-out and the inactivity timer.
   *
   * Steps:
   * 1. Clears both inactivity timers.
   * 2. Removes the multi-tab activity sync key from `localStorage`.
   * 3. Calls `AuthStore.logout()`, which clears `auth_token`, `user_first_name`,
   *    and `user_last_name` from `localStorage` and resets NgRx signal state.
   * 4. Navigates to `/login` (appending `?expired=true` for inactivity-triggered logouts).
   *
   * @param expired `true` when triggered by the inactivity timer; `false` for manual sign-out.
   */
  private _performLogout(expired: boolean): void {
    this._clearTimers();
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    this.authStore.logout();
    const queryParams = expired ? { expired: 'true' } : {};
    void this.appNav.navigate('/login', { queryParams });
  }

  private _attachListeners(): void {
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, this._boundActivityHandler, { passive: true });
    }
    globalThis.addEventListener('storage', this._boundStorageHandler);
  }

  private _detachListeners(): void {
    for (const event of ACTIVITY_EVENTS) {
      document.removeEventListener(event, this._boundActivityHandler);
    }
    globalThis.removeEventListener('storage', this._boundStorageHandler);
  }
}
